//
// # Symbol Pin Context Menu
//
// Displays actions when right-clicking on a symbol pin.

import type { Editor } from "./editor";
import type { model } from "GenAlphaModel";
import { Palette } from "DrawCore/src/style";
import { BaseContextMenu } from "./baseContextMenu";
import type { ContextMenuStyleConfig } from "./baseContextMenu";
import { inferLabelPosition } from "EditorCore/src/content/symbol/symbol";

export class SymbolPinContextMenu extends BaseContextMenu {
  private currentPinData: model.SymbolPin | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "140px",
    maxWidth: "250px",
  };

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header-light {
        padding: 6px 12px;
        color: ${p.textTarget};
        font-size: 11px;
      }
    `;
  }

  protected clearState(): void {
    this.currentPinData = null;
  }

  // Show context menu for a symbol pin at the given page position.
  show = (pinData: model.SymbolPin, pageX: number, pageY: number): void => {
    this.editor.hideAllContextMenus();
    this.currentPinData = pinData;
    this.ensureMenuElement();

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header-light">Pin: ${pinData.portName}</div>`;
    content += `</div>`;
    content += `<div class="context-menu-separator"></div>`;
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="edit-pin">Edit Pin...</div>`;
    content += `<div class="context-menu-item" data-action="delete-pin">Delete</div>`;
    content += `</div>`;

    this.displayMenu(content, pageX, pageY);
    this.setupMenuItemHandlers();
  };

  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      const element = item as HTMLElement;
      element.onclick = (e) => {
        e.stopPropagation();
        const action = element.getAttribute("data-action");

        if (action === "edit-pin") {
          this.handleEditPin();
        } else if (action === "delete-pin") {
          this.handleDeletePin();
        }

        this.hide();
      };
    });
  };

  private handleEditPin = (): void => {
    if (!this.currentPinData) return;
    const { portName, width, direction, id, labelFmt } = this.currentPinData;
    const labelPosition = labelFmt ? inferLabelPosition(labelFmt) : "left";
    this.editor.platform.notifyEditSymbolPin({
      portName,
      width,
      dir: direction,
      labelPosition,
      id,
    });
  };

  private handleDeletePin = (): void => {
    const { editor } = this;
    const result = editor.content.deleteSelected();
    if (result) editor.showItemFeedback("Deleted", 1);
  };
}
